import axios, { AxiosError } from 'axios';
import { logError } from './logger';

export type ApiError = {
  message: string;
  status?: number;
  details?: any;
}

/**
 * Standardizes error handling for API calls
 */
export const handleApiError = (error: unknown): ApiError => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError;
    
    // Handle Axios errors
    if (axiosError.response) {
      // Server responded with error status
      const responseData = axiosError.response.data as Record<string, any>;
      return {
        message: typeof responseData === 'object' && responseData?.message ? responseData.message : 'Server error occurred',
        status: axiosError.response.status,
        details: axiosError.response.data
      };
    } else if (axiosError.request) {
      // Request made but no response received
      return {
        message: 'Network error - no response from server',
        details: { request: axiosError.request }
      };
    } else {
      // Request setup error
      return {
        message: axiosError.message || 'Error setting up request',
      };
    }
  }
  
  // Handle generic errors
  if (error instanceof Error) {
    logError(error.message);
    return {
      message: error.message || 'An unexpected error occurred',
      details: error
    };
  }
  
  // Unknown error type
  return {
    message: 'An unknown error occurred',
    details: error
  };
};

/**
 * Extract user-friendly error message from API response
 */
export const getUserFriendlyErrorMessage = (apiError: ApiError): string => {
  // Handle common status codes
  if (apiError.status) {
    switch (apiError.status) {
      case 400:
        return 'Invalid request data';
      case 401:
        return 'You need to login again';
      case 403:
        return 'You don\'t have permission to access this resource';
      case 404:
        return 'The requested resource was not found';
      case 429:
        return 'Too many requests, please try again later';
      case 500:
      case 502:
      case 503:
        return 'Server error, please try again later';
    }
  }

  // Use the provided message or a default one
  return apiError.message || 'An error occurred';
};
